import { ConfiguratorTreeItemModel } from "src/api/generated/erp/configurator/configuratorTree/configuratorTreeItemModel";
import { ConfiguratorInput_InputValue_Scalar } from "src/api/generated/io/aavo/applications/db/erp/types/configuratorInput";
import { TransformedConfigurationComponent } from "src/api/generated/erp/configurator/componentTransformation/model/transformedConfigurationComponent.ts";

/**
 * Immutable update function for a single item in the configurator tree.
 * Returns a new root item state with the specified item updated.
 */
export function updateConfiguratorTreeItemState(
	rootItemState: ConfiguratorTreeItemModel,
	keyToUpdate: string,
	updateFn: (state: ConfiguratorTreeItemModel) => ConfiguratorTreeItemModel,
): ConfiguratorTreeItemModel {
	if (keyToUpdate === rootItemState.key) {
		return updateFn(rootItemState);
	}

	const updatedChildren = rootItemState.children.map((child) =>
		updateConfiguratorTreeItemState(child, keyToUpdate, updateFn),
	);
	return {
		...rootItemState,
		children: updatedChildren,
	};
}

export function regenerateKeysForAllTreeItems(rootItemState: ConfiguratorTreeItemModel): ConfiguratorTreeItemModel {
	const updatedChildren = rootItemState.children.map((child) => regenerateKeysForAllTreeItems(child));

	return {
		...rootItemState,
		key: crypto.randomUUID(),
		children: updatedChildren,
	};
}

export function updateComponentValues(
	components: TransformedConfigurationComponent[],
	componentValuesByNames: Record<string, ConfiguratorInput_InputValue_Scalar>,
): TransformedConfigurationComponent[] {
	return components.map((component) => {
		const componentValue = componentValuesByNames[component.name];
		if (componentValue && component.type === "field") {
			return {
				...component,
				currentValue: componentValue,
			};
		}
		return component;
	});
}

export function addChildToTreeItem(
	parentItem: ConfiguratorTreeItemModel,
	newChild: ConfiguratorTreeItemModel,
): ConfiguratorTreeItemModel {
	const newChildComponentPosition = parentItem.components.findIndex(
		(comp) => comp.name === newChild.selfConfiguratorComponentName,
	);

	const firstExistingChildToPositionAfterNewChild = parentItem.children.findIndex((existingChild) => {
		const existingChildComponentPosition = parentItem.components.findIndex(
			(comp) => comp.name === existingChild.selfConfiguratorComponentName,
		);

		return existingChildComponentPosition > newChildComponentPosition;
	});

	const newChildIndex =
		firstExistingChildToPositionAfterNewChild !== -1 ?
			firstExistingChildToPositionAfterNewChild
		:	parentItem.children.length;

	const newChildren = [...parentItem.children];
	newChildren.splice(newChildIndex, 0, newChild);

	return updateSubConfiguratorListIndexes({
		...parentItem,
		children: newChildren,
	});
}

export function updateSubConfiguratorListIndexes(parentItem: ConfiguratorTreeItemModel): ConfiguratorTreeItemModel {
	const listComponentNames = new Set(
		parentItem.components
			.filter((component) => component.type === "subConfiguratorList")
			.map((component) => component.name),
	);
	const nextIndexByComponentName = new Map<string, number>();
	const children = parentItem.children.map((child) => {
		if (child.configuratorType?.type !== "subConfigurator") return child;

		const componentName = child.selfConfiguratorComponentName;
		if (componentName == null || !listComponentNames.has(componentName)) {
			return {
				...child,
				configuratorType: {
					...child.configuratorType,
					subConfiguratorListIndex: null,
				},
			};
		}

		const index = nextIndexByComponentName.get(componentName) ?? 1;
		nextIndexByComponentName.set(componentName, index + 1);
		return {
			...child,
			configuratorType: {
				...child.configuratorType,
				subConfiguratorListIndex: index,
			},
		};
	});

	return {
		...parentItem,
		children: children,
	};
}

export function getSubConfiguratorListIndexChangedChildKeys(
	previousParentItem: ConfiguratorTreeItemModel,
	updatedParentItem: ConfiguratorTreeItemModel,
): string[] {
	const previousIndexByChildKey = new Map(
		previousParentItem.children.map((child) => [
			child.key,
			child.configuratorType.type === "subConfigurator" ? child.configuratorType.subConfiguratorListIndex : null,
		]),
	);

	return updatedParentItem.children
		.filter(
			(child) =>
				child.configuratorType.type === "subConfigurator" &&
				previousIndexByChildKey.get(child.key) !== child.configuratorType.subConfiguratorListIndex,
		)
		.map((child) => child.key);
}
